import { z } from 'zod';
import { REGEX_WITH_ACCENTEDCHARS } from '../../../../utils/validators/regex';

// Define the Zod schema for enrollment
export const enrollmentSchema = (enrollment, userPermissions) =>
    z
        .object({
            enrollmentBenefitEffectiveStartDateUI: z.string().min(10, {
                message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnter.benefitStartDate',
            }),
            enrollmentBenefitEffectiveEndDateUI: z.string().min(10, {
                message: 'error.member.enrollment_list.premiumhistory.enrollee.enrollSubscriberEndDate',
            }),

            enrolleeDataDtoList: z.array(
                z.object({
                    firstName: z
                        .string()
                        .min(1, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnter.firstName',
                        })
                        .regex(REGEX_WITH_ACCENTEDCHARS, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.valid.firstName',
                        }),
                    middleName: z
                        .string()
                        .regex(REGEX_WITH_ACCENTEDCHARS, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.valid.middleName',
                        })
                        .nullable()
                        .optional(),
                    lastName: z
                        .string()
                        .min(1, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnter.lastName',
                        })
                        .regex(REGEX_WITH_ACCENTEDCHARS, {
                            message: 'error.member.enrollment_list.premiumhistory.enrollee.valid.lastName',
                        }),
                    genderLookupLabel: z.string(),
                    taxIdentificationNumber: z.string().nullable(),
                    birthDate: z.string().optional(),
                    enrolleeEffectiveStartDateUI: z.string().min(10, {
                        message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnter',
                    }),
                    enrolleeEffectiveEndDateUI: z.string().min(10, {
                        message: 'error.member.enrollment_list.premiumhistory.enrollee.pleaseEnterEndDate',
                    }),
                }),
            ),
            enrollmentPremiumDtoList: z.array(
                z
                    .object({
                        month: z.number(),
                        year: z.number(),
                        slcspPremiumAmount: z.number().optional(),
                        isFinancial: z.boolean().optional(),
                        isAptcEligible: z.boolean().optional(),
                        isStateSubsidyEligible: z.boolean().optional(),
                        aptcAmount: z.number().optional(),
                        grossPremiumAmount: z.number().optional(),
                        maxAptc: z.number().optional(),
                        maxStateSubsidy: z.number().optional(),
                        minimumPremium: z.number().optional(),
                        netPremiumAmount: z.number().optional(),
                        stateSubsidyAmount: z.number().optional(),
                    })
                    .superRefine((data, customError) => aptcRequiredValidation(data, customError, enrollment)),
            ),
        })
        .superRefine((data, customError) => DateValidations(data, customError, enrollment));

const aptcRequiredValidation = (data, customError, enrollment) => {
    const ehbPercent = enrollment.ehbPercentage;
    const enrollmentPremium = enrollment.enrollmentPremiumDtoList[data.month - 1];
    const premium = { ...data };
    if (data.isFinancial) {
        if (data.maxAptc === undefined) {
            customError.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['maxAptc'],
                message: 'error.member.enrollment_list.premiumhistory.validNumber',
            });
        }
        if (data.aptcAmount === undefined) {
            customError.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['aptcAmount'],
                message: 'error.member.enrollment_list.premiumhistory.validNumber',
            });
        }

        if (ehbPercent !== null && ehbPercent !== 0) {
            if (Number((premium.grossPremiumAmount * ehbPercent).toFixed(2)) < Number(premium.aptcAmount.toFixed(2))) {
                customError.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['aptcAmount'],
                    message: 'error.member.enrollment_list.premiumhistory.aptc.lessOrEqual2',
                });
            }
        } else {
            if (premium.grossPremiumAmount < premium.aptcAmount) {
                customError.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['aptcAmount'],
                    message: 'error.member.enrollment_list.premiumhistory.aptc.lessOrEqual2',
                });
            }
        }
        if (premium.maxAptc !== undefined && premium.aptcAmount > premium.maxAptc) {
            customError.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['aptcAmount'],
                message: 'error.member.enrollment_list.premiumhistory.aptc.lessOrEqual',
            });
            customError.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['maxAptc'],
                message: 'error.member.enrollment_list.premiumhistory.caps.greaterOrEqual',
            });
        }

        if (premium.isStateSubsidyEligible) {
            if (premium.stateSubsidyAmount === undefined) {
                customError.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['stateSubsidyAmount'],
                    message: 'error.member.enrollment_list.premiumhistory.validNumber',
                });
            }
            if (premium.maxStateSubsidy === undefined) {
                customError.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['maxStateSubsidy'],
                    message: 'error.member.enrollment_list.premiumhistory.validNumber',
                });
            }

            if (premium.maxStateSubsidy !== undefined && premium.stateSubsidyAmount > premium.maxStateSubsidy) {
                customError.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['stateSubsidyAmount'],
                    message: 'error.member.enrollment_list.premiumhistory.stateSubsidy.lessOrEqual',
                });
                customError.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['maxStateSubsidy'],
                    message: 'error.member.enrollment_list.premiumhistory.caps.greaterOrEqual',
                });
            }

            if (ehbPercent !== null && ehbPercent !== 0) {
                if (
                    Number((premium.grossPremiumAmount * ehbPercent).toFixed(2)) <
                    Number(premium.stateSubsidyAmount.toFixed(2))
                ) {
                    customError.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['stateSubsidyAmount'],
                        message: 'error.member.enrollment_list.premiumhistory.caps.lessOrEqual',
                    });
                }
            } else {
                if (premium.grossPremiumAmount < premium.stateSubsidyAmount) {
                    customError.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ['stateSubsidyAmount'],
                        message: 'error.member.enrollment_list.premiumhistory.caps.lessOrEqual',
                    });
                }
            }

            if (premium.grossPremiumAmount !== undefined) {
                let enrolleeCount = enrollment.enrolleeDataDtoList.length;
                let aptcAmount = 0;
                let minStateSubsidy = 0;

                if (premium.maxAptc !== undefined) {
                    aptcAmount = premium.maxAptc;
                }
                if (premium.statePremiumCreditAmt !== undefined) {
                    minStateSubsidy = premium.grossPremiumAmount - (aptcAmount + premium.statePremiumCreditAmt);
                } else {
                    minStateSubsidy = premium.grossPremiumAmount - (aptcAmount + enrolleeCount);
                }
                if (premium.maxAptc !== undefined) {
                    if (
                        premium.stateSubsidyAmount > minStateSubsidy &&
                        premium.stateSubsidyAmount !== enrollmentPremium.stateSubsidyAmount
                    ) {
                        if (premium.stateSubsidyAmount > 0) {
                            customError.addIssue({
                                code: z.ZodIssueCode.custom,
                                path: ['stateSubsidyAmount'],
                                message: 'error.member.enrollment_list.premiumhistory.premiumSubsidy.lessOrEqual2',
                            });
                        }
                    }
                } else {
                    if (
                        premium.stateSubsidyAmount > minStateSubsidy &&
                        premium.stateSubsidyAmount !== enrollmentPremium.stateSubsidyAmount
                    ) {
                        customError.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: ['stateSubsidyAmount'],
                            message: 'error.member.enrollment_list.premiumhistory.premiumSubsidy.lessOrEqual',
                        });
                    }
                }
            }
        }
    }
    return z.NEVER;
};

const DateValidations = (data, customError, enrollment) => {
    const birthDate = new Date(data.enrolleeDataDtoList?.[0]?.birthDate || '');
    const enrollmentBenefitEffectiveStartDateUI = new Date(data.enrollmentBenefitEffectiveStartDateUI);
    if (birthDate > enrollmentBenefitEffectiveStartDateUI) {
        customError.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['enrollmentBenefitEffectiveStartDateUI'],
            message: 'error.member.enrollment_list.premiumhistory.enrollee.early.benefitStartDate',
        });
    }

    if (data.enrollmentBenefitEffectiveEndDateUI === data.enrollmentBenefitEffectiveStartDateUI) {
        if (enrollment.enrollmentBenefitEffectiveStartDateUI !== data.enrollmentBenefitEffectiveStartDateUI) {
            customError.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['enrollmentBenefitEffectiveStartDateUI'],
                message: 'error.member.enrollment_list.premiumhistory.enrollee.cancellation.benefitStartDate',
            });
        }
        if (enrollment.enrollmentBenefitEffectiveEndDateUI !== data.enrollmentBenefitEffectiveEndDateUI) {
            customError.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['enrollmentBenefitEffectiveEndDateUI'],
                message: 'error.member.enrollment_list.premiumhistory.enrollee.enrollSubscriber',
            });
        }
    }
};
